import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReferralPanel from "@/components/shared/ReferralPanel";
import MyMatchScorePanel from "@/components/shared/MyMatchScorePanel";
import AppealPanel from "@/components/shared/AppealPanel";
import AvailabilityCalendar from "@/components/shared/AvailabilityCalendar";
import { PageHero, StatHero, ActionTile, StatusPill } from "@/components/shared/Visual";
import { Briefcase, Calendar, Users2, ShieldCheck, MessageSquare, ArrowUpRight, Activity } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function StaffDashboard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [negotiations, setNegotiations] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [j, t, b, n] = await Promise.all([
          api.get("/jobs").catch(() => ({ data: [] })),
          api.get("/teams/mine").catch(() => ({ data: [] })),
          api.get("/bookings/mine").catch(() => ({ data: [] })),
          api.get("/negotiations").catch(() => ({ data: [] })),
        ]);
        setJobs(j.data || []);
        setTeams(t.data || []);
        setBookings(b.data || []);
        setNegotiations(n.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const activeNegotiations = negotiations.filter((n) => !["completed", "cancelled"].includes(n.status)).length;

  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <PageHero
        eyebrow="Professional Console"
        title={`Welcome, ${user?.full_name?.split(" ")[0] || ""}`}
        sub={<span><span className="ss-id">{user?.secusource_id}</span></span>}
        right={<StatusPill kind={user?.status === "approved" ? "active" : "standby"}>{user?.status || "pending"}</StatusPill>}
      />

      <AppealPanel user={user} onDone={() => {}} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Link to="/staff/my-job" className="card-dark card-dark-hover p-4 flex items-center gap-3 elite-glow" data-testid="staff-tile-my-job">
          <div className="w-10 h-10 rounded-md bg-gold-500/15 text-gold-400 flex items-center justify-center"><Activity className="w-4 h-4" /></div>
          <div>
            <div className="font-outfit font-bold txt-hi text-sm">My Job Today</div>
            <div className="eyebrow mt-0.5">Check-in · timer</div>
          </div>
        </Link>
        <Link to="/staff/jobs" className="card-dark card-dark-hover p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-brand/15 text-brand flex items-center justify-center"><Briefcase className="w-4 h-4" /></div>
          <div>
            <div className="font-outfit font-bold txt-hi text-sm">Jobs Board</div>
            <div className="eyebrow mt-0.5">Open · match</div>
          </div>
        </Link>
        <Link to="/staff/bookings" className="card-dark card-dark-hover p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-violet-500/15 text-violet-400 flex items-center justify-center"><Calendar className="w-4 h-4" /></div>
          <div>
            <div className="font-outfit font-bold txt-hi text-sm">Bookings</div>
            <div className="eyebrow mt-0.5">Upcoming</div>
          </div>
        </Link>
        <Link to="/staff/profile" className="card-dark card-dark-hover p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-cyan-500/15 text-cyan-400 flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></div>
          <div>
            <div className="font-outfit font-bold txt-hi text-sm">Profile</div>
            <div className="eyebrow mt-0.5">Docs · rank</div>
          </div>
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatHero label="Open jobs" value={jobs.length} icon={Briefcase} tone="brand" to="/staff/jobs" testId="stat-jobs" />
        <StatHero label="My bookings" value={bookings.length} icon={Calendar} tone="emerald" to="/staff/bookings" testId="stat-bookings" />
        <StatHero label="My teams" value={teams.length} icon={Users2} tone="violet" to="/staff/teams" testId="stat-teams" />
        <StatHero label="Negotiations" value={activeNegotiations} icon={MessageSquare} tone="cyan" to="/staff/negotiations" testId="stat-negotiations" />
        <StatHero label="Completed" value={user?.completed_jobs || 0} icon={ShieldCheck} tone="brand" to="/staff/profile" testId="stat-completed" />
      </div>

      <ReferralPanel role="professional" />

      {user?.id && <AvailabilityCalendar userId={user.id} editable={true} testIdPrefix="staff-avail" />}

      <MyMatchScorePanel />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-dark p-5">
          <div className="flex justify-between items-center mb-4">
            <div className="eyebrow eyebrow-brand">Latest Jobs</div>
            <Link to="/staff/jobs" className="text-brand text-xs font-manrope font-semibold flex items-center gap-1">All <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          {jobs.length === 0 ? <div className="txt-lo text-sm font-manrope">No open jobs.</div> : (
            <div className="space-y-2">
              {jobs.slice(0, 3).map((j) => (
                <Link key={j.id} to="/staff/jobs" className="card-inner p-3 block hover:bg-white/5">
                  <div className="font-outfit font-semibold txt-hi text-sm">{j.title}</div>
                  <div className="text-xs txt-lo font-manrope mt-0.5">{j.location} · {j.date} · €{j.rate_of_pay}/hr</div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="card-dark p-5">
          <div className="flex justify-between items-center mb-4">
            <div className="eyebrow eyebrow-brand">Active Rooms</div>
            <Link to="/staff/negotiations" className="text-brand text-xs font-manrope font-semibold flex items-center gap-1">All <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          {negotiations.length === 0 ? (
            <div className="txt-lo text-sm font-manrope">No active rooms.</div>
          ) : (
            <div className="space-y-2">
              {negotiations.slice(0, 3).map((n) => (
                <Link key={n.id} to={`/negotiations/${n.id}`} className="card-inner p-3 block hover:bg-white/5" data-testid={`negotiation-quick-${n.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-outfit font-semibold txt-hi text-sm">{n.company_name || "Company"}</div>
                    <StatusPill kind={n.status === "completed" ? "active" : "live"}>{(n.status || "open").replace(/_/g, " ")}</StatusPill>
                  </div>
                  <div className="text-xs txt-lo font-manrope mt-0.5">{n.job_title || "Booking"} · {n.created_at ? new Date(n.created_at).toLocaleDateString() : "—"}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
